/**
 * Форма редактирования — второй переиспользуемый компонент админки.
 *
 * Поля она строит ПО ФОРМЕ САМОГО ЗНАЧЕНИЯ, а не по описанию полей,
 * заведённому рядом. Описание пришлось бы держать в синхронности с
 * zod-схемой из `@lust/shared`, а два источника правды об одной структуре
 * расходятся ровно тогда, когда в конфиг добавляют поле: схема о нём знает,
 * форма — нет, и поле молча невозможно отредактировать. Здесь новое поле
 * появляется в форме само.
 *
 * Отсюда же второй режим — «JSON». Из формы по значению нельзя добавить
 * элемент массива или ключ, которого в значении ещё нет; текстовый режим
 * это закрывает. Проверка у обоих режимов одна и та же — та самая
 * zod-схема ключа, — поэтому «через JSON» нельзя сохранить то, чего
 * не пропускает форма.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'

export interface EditFormProps {
  /** Текущее значение. Форма полей строится по его структуре. */
  value: unknown
  /** Человеческие подписи по пути поля (`win.gold`). Только оформление. */
  labels?: Record<string, string>
  /** Пояснения под полем. */
  hints?: Record<string, string>
  /** Проверка перед отправкой: текст ошибки или null. */
  validate: (draft: unknown) => string | null
  onSubmit: (draft: unknown) => Promise<void>
  submitLabel?: string
  disabled?: boolean
  /** Показывается слева от кнопки: версия, владелец строки. */
  meta?: ReactNode
}

interface Leaf {
  path: string[]
  /** Путь строкой: он же ключ черновика и ключ в словаре подписей. */
  id: string
  value: string | number | boolean
}

export function EditForm({
  value,
  labels = {},
  hints = {},
  validate,
  onSubmit,
  submitLabel = 'Apply',
  disabled = false,
  meta,
}: EditFormProps): JSX.Element {
  const original = useMemo(() => JSON.stringify(value), [value])
  const [mode, setMode] = useState<'fields' | 'json'>('fields')
  const [drafts, setDrafts] = useState<Record<string, string>>(() => draftsOf(value))
  const [json, setJson] = useState(() => pretty(value))
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Значение сменилось снаружи (сохранили другой ключ, перезагрузили
  // список) — черновик обязан начаться заново, иначе форма показывает
  // старые числа поверх новых данных.
  useEffect(() => {
    setDrafts(draftsOf(value))
    setJson(pretty(value))
    setError(null)
    setSaved(false)
  }, [original])

  const leaves = useMemo(() => leavesOf(value), [original])

  const draft = useMemo(() => {
    if (mode === 'json') {
      try {
        return { ok: true as const, value: JSON.parse(json) as unknown }
      } catch (err) {
        return { ok: false as const, message: `JSON does not parse: ${(err as Error).message}` }
      }
    }
    return { ok: true as const, value: applyDrafts(value, leaves, drafts) }
  }, [mode, json, drafts, leaves, original])

  const changed = draft.ok && JSON.stringify(draft.value) !== original

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setSaved(false)

    if (!draft.ok) {
      setError(draft.message)
      return
    }

    /**
     * Проверка на клиенте — той же схемой, что и на сервере. Она НЕ заменяет
     * серверную: клиент можно обойти, и на этот случай маршрут проверяет
     * значение сам. Она нужна ради скорости ответа — ошибку в поле видно
     * до похода в сеть.
     */
    const invalid = validate(draft.value)
    if (invalid !== null) {
      setError(invalid)
      return
    }

    setError(null)
    setSaving(true)
    try {
      await onSubmit(draft.value)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 'var(--gap)' }}>
        <div className="btn-group">
          <button
            type="button"
            className={mode === 'fields' ? 'is-active' : undefined}
            onClick={() => setMode('fields')}
          >
            Fields
          </button>
          <button
            type="button"
            className={mode === 'json' ? 'is-active' : undefined}
            onClick={() => {
              // Переходя в JSON, забираем с собой текущие правки полей,
              // иначе режим-переключатель работал бы как «отменить».
              if (draft.ok) setJson(pretty(draft.value))
              setMode('json')
            }}
          >
            JSON
          </button>
        </div>
        {meta}
      </div>

      {mode === 'fields' ? (
        <div className="form-grid">
          {leaves.map((leaf) => (
            <label className="field" key={leaf.id}>
              <span className="field__label">{labels[leaf.id] ?? leaf.id}</span>
              {typeof leaf.value === 'boolean' ? (
                <select
                  className="select"
                  value={drafts[leaf.id] ?? String(leaf.value)}
                  disabled={disabled || saving}
                  onChange={(e) => setDrafts({ ...drafts, [leaf.id]: e.target.value })}
                >
                  <option value="true">yes</option>
                  <option value="false">no</option>
                </select>
              ) : (
                <input
                  className={`input${fieldInvalid(leaf, drafts[leaf.id]) ? ' input--invalid' : ''}`}
                  value={drafts[leaf.id] ?? String(leaf.value)}
                  inputMode={typeof leaf.value === 'number' ? 'numeric' : undefined}
                  disabled={disabled || saving}
                  onChange={(e) => setDrafts({ ...drafts, [leaf.id]: e.target.value })}
                />
              )}
              {hints[leaf.id] !== undefined && <span className="field__hint">{hints[leaf.id]}</span>}
            </label>
          ))}
        </div>
      ) : (
        <textarea
          className={`textarea${draft.ok ? '' : ' textarea--invalid'}`}
          value={json}
          spellCheck={false}
          disabled={disabled || saving}
          onChange={(e) => setJson(e.target.value)}
        />
      )}

      {error !== null && (
        <div className="notice notice--error" style={{ marginTop: 'var(--gap)' }}>
          {error}
        </div>
      )}
      {saved && error === null && (
        <div className="notice notice--ok" style={{ marginTop: 'var(--gap)' }}>
          Saved. The game picks the new value up immediately — the cache was dropped.
        </div>
      )}

      <div className="row" style={{ marginTop: 'var(--gap)' }}>
        <button className="btn btn--primary" type="submit" disabled={disabled || saving || !changed}>
          {saving ? 'Saving…' : submitLabel}
        </button>
        {changed && (
          <button
            className="btn btn--ghost"
            type="button"
            disabled={saving}
            onClick={() => {
              setDrafts(draftsOf(value))
              setJson(pretty(value))
              setError(null)
            }}
          >
            Discard changes
          </button>
        )}
        {!changed && !saving && <span className="muted">No changes</span>}
      </div>
    </form>
  )
}

/* ─────────────────────────── разбор значения ───────────────────────────── */

function leavesOf(value: unknown, prefix: string[] = []): Leaf[] {
  if (value === null || value === undefined) return []

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => leavesOf(item, [...prefix, String(index)]))
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(([key, item]) =>
      leavesOf(item, [...prefix, key]),
    )
  }

  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return [{ path: prefix, id: prefix.join('.'), value }]
  }

  return []
}

function draftsOf(value: unknown): Record<string, string> {
  const result: Record<string, string> = {}
  for (const leaf of leavesOf(value)) result[leaf.id] = String(leaf.value)
  return result
}

/**
 * Черновик поверх исходного значения.
 *
 * Клонируем оригинал и правим по путям, а не собираем объект заново.
 * Так сохраняются массивы (иначе `dialogueChoices` стал бы объектом
 * `{0:…,1:…}` и не прошёл бы схему) и всё, что форма показать не умеет.
 */
function applyDrafts(value: unknown, leaves: Leaf[], drafts: Record<string, string>): unknown {
  const clone: unknown = JSON.parse(JSON.stringify(value ?? null))

  for (const leaf of leaves) {
    const raw = drafts[leaf.id]
    if (raw === undefined) continue
    setAtPath(clone, leaf.path, coerce(raw, leaf.value))
  }

  return clone
}

function coerce(raw: string, sample: string | number | boolean): unknown {
  if (typeof sample === 'number') {
    const trimmed = raw.trim()
    // Пустое поле — это не ноль. Отдаём NaN, и схема скажет «ожидалось
    // число»: молчаливый ноль в награде за победу нашёлся бы не сразу.
    return trimmed === '' ? Number.NaN : Number(trimmed)
  }
  if (typeof sample === 'boolean') return raw === 'true'
  return raw
}

function setAtPath(target: unknown, path: string[], value: unknown): void {
  let node = target
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (key === undefined || node === null || typeof node !== 'object') return
    node = (node as Record<string, unknown>)[key]
  }
  const last = path[path.length - 1]
  if (last === undefined || node === null || typeof node !== 'object') return
  ;(node as Record<string, unknown>)[last] = value
}

function fieldInvalid(leaf: Leaf, raw: string | undefined): boolean {
  if (raw === undefined || typeof leaf.value !== 'number') return false
  const trimmed = raw.trim()
  return trimmed === '' || Number.isNaN(Number(trimmed))
}

function pretty(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2)
}
